import React, { useEffect, useRef, useState } from "react";

import type { TabType } from "@/components/index/navbar-refund";

import { fetchDiscoveryProducts } from "@/api/discovery";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarRefund from "@/components/index/navbar-refund";
import { Button } from "@/components/ui/button";
import LoginPromptModalDiscovery from "@/components/ui/login-prompt-modal-discovery";
import { Skeleton } from "@/components/ui/skeleton";
import useAuthStore from "@/store/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

// Skeleton component for product cards
function ProductCardSkeleton() {
	return (
		<div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
			{/* Image placeholder */}
			<Skeleton className="w-full aspect-[4/5]" />
			{/* Text content */}
			<div className="p-3 flex flex-col gap-2">
				<Skeleton className="h-3 w-20" />
				<Skeleton className="h-4 w-32" />
				<Skeleton className="h-4 w-16" />
			</div>
		</div>
	);
}

function Discovery() {
	const modalRef = useRef<HTMLDivElement>(null);
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);
	useEffect(() => {
		if (showLoginPrompt && modalRef.current) {
			modalRef.current.focus();
		}
	}, [showLoginPrompt]);

	const lastProductCountRef = useRef(0);
	const [products, setProducts] = useState<any[]>([]);
	useEffect(() => {
		if (lastProductCountRef.current > 0 && products.length > lastProductCountRef.current) {
			const newProductIndex = lastProductCountRef.current;
			const productElements = document.querySelectorAll("[data-product-card]");
			if (productElements[newProductIndex]) {
				productElements[newProductIndex].scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}
	}, [products]);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const [activeTab, setActiveTab] = useState<TabType>("All" as unknown as TabType);
	const [activeCategoryId, setActiveCategoryId] = useState<string>("");

	const [search, setSearch] = useState("");
	const [limit] = useState(20);
	const [page, setPage] = useState(1);
	const [totalItems, setTotalItems] = useState(0);

	// Category-specific cache: stores products, pagination, and scroll position per category
	const categoryCacheRef = useRef<Record<string, { products: any[]; totalItems: number; page: number; scrollY: number }>>({});

	// Cache discovery state to sessionStorage so navigating back restores it
	const cacheKey = "discovery-cache";
	const isRestoredRef = useRef(false);

	// Restore cached state on mount (only once)
	useEffect(() => {
		try {
			const cached = sessionStorage.getItem(cacheKey);
			if (cached) {
				const { products: cachedProducts, totalItems: cachedTotal, page: cachedPage, scrollY: cachedScrollY, search: cachedSearch, activeCategoryId: cachedCategory, activeTab: cachedTab } = JSON.parse(cached);
				setProducts(cachedProducts);
				setTotalItems(cachedTotal);
				setPage(cachedPage);
				setSearch(cachedSearch || "");
				setActiveCategoryId(cachedCategory || "");
				if (cachedTab)
					setActiveTab(cachedTab);
				setLoading(false);
				isRestoredRef.current = true;
				lastProductCountRef.current = 0; // Reset so auto-scroll doesn't fire on restore
				// Also populate category cache so category switching works after navigation
				const categoryKey = cachedCategory || "all";
				categoryCacheRef.current[categoryKey] = { products: cachedProducts, totalItems: cachedTotal, page: cachedPage, scrollY: cachedScrollY || 0 };
				// Restore scroll position after products render
				requestAnimationFrame(() => {
					window.scrollTo(0, cachedScrollY || 0);
				});
				// Clear after restoring — saveDiscoveryState() will re-save before next navigation
				sessionStorage.removeItem(cacheKey);
			}
		}
		catch {
			// Ignore cache errors
		}
	}, []);

	// Cache for full category results when API returns all products at once
	const fullCategoryDataRef = useRef<Record<string, { allProducts: any[]; totalItems: number }>>({});

	useEffect(() => {
		// Skip the initial fetch if we restored from cache
		if (isRestoredRef.current) {
			isRestoredRef.current = false;
			return;
		}

		// Check if we have cached products for this category
		const categoryKey = activeCategoryId || "all";
		const cachedCategory = categoryCacheRef.current[categoryKey];

		// Use cached category if search is empty and we're on page 1 (just switched)
		if (cachedCategory && !search && page === 1) {
			setProducts(cachedCategory.products);
			setTotalItems(cachedCategory.totalItems);
			setLoading(false);
			// Set page to cached value so "See more" continues from the right page
			// Mark as restored so the page change doesn't trigger another fetch
			if (cachedCategory.page > 1) {
				isRestoredRef.current = true;
				setPage(cachedCategory.page);
			}
			return;
		}

		// Check if we already have all products for this category from a previous full fetch
		const fullData = fullCategoryDataRef.current[categoryKey];
		if (fullData && !search && page >= 1) {
			const sliced = fullData.allProducts.slice(0, page * limit);
			setProducts(sliced);
			setTotalItems(fullData.totalItems);
			setLoading(false);
			// Update category cache
			categoryCacheRef.current[categoryKey] = { products: sliced, totalItems: fullData.totalItems, page, scrollY: window.scrollY };
			return;
		}

		setLoading(true);
		fetchDiscoveryProducts({
			limit,
			page,
			search,
			category: activeCategoryId
		})
			.then((data) => {
				let items = [];
				let total = 0;
				if (Array.isArray(data?.data?.items)) {
					items = data.data.items;
					total = data.data.meta?.totalItems || 0;
				}
				else if (Array.isArray(data?.items)) {
					items = data.items;
					total = data.meta?.totalItems || 0;
				}
				else if (Array.isArray(data)) {
					items = data;
					total = data.length;
				}

				// If API returned more items than the limit, it sent all products at once
				// Store the full list and only display one page worth
				if (page === 1 && items.length > limit) {
					const actualTotal = total || items.length;
					fullCategoryDataRef.current[categoryKey] = { allProducts: items, totalItems: actualTotal };
					const sliced = items.slice(0, limit);
					setProducts(sliced);
					setTotalItems(actualTotal);
					if (!search) {
						categoryCacheRef.current[categoryKey] = { products: sliced, totalItems: actualTotal, page: 1, scrollY: 0 };
					}
				}
				else if (page === 1) {
					setProducts(items);
					if (!search) {
						categoryCacheRef.current[categoryKey] = { products: items, totalItems: total, page: 1, scrollY: 0 };
					}
				}
				else {
					// Filter out duplicates before appending
					setProducts((prev) => {
						const existingIds = new Set(prev.map((p: any) => p.id));
						const newItems = items.filter((item: any) => !existingIds.has(item.id));
						const updatedProducts = [...prev, ...newItems];
						// Update cache with new products if no search is active
						if (!search) {
							categoryCacheRef.current[categoryKey] = { products: updatedProducts, totalItems: total, page, scrollY: window.scrollY };
						}
						return updatedProducts;
					});
				}
				setTotalItems(total || items.length);
				setLoading(false);
			})
			.catch((err) => {
				setError("Failed to load products");
				setLoading(false);
				console.error("Discovery API error:", err);
			});
	}, [limit, page, search, activeCategoryId]);

	// Save discovery state to sessionStorage before navigating away
	const saveDiscoveryState = () => {
		// Also update the current category's scroll position in cache
		const currentKey = activeCategoryId || "all";
		if (categoryCacheRef.current[currentKey]) {
			categoryCacheRef.current[currentKey].scrollY = window.scrollY;
		}
		sessionStorage.setItem(cacheKey, JSON.stringify({
			products,
			totalItems,
			page,
			scrollY: window.scrollY,
			search,
			activeCategoryId,
			activeTab
		}));
	};

	const isAuthenticated = useAuthStore(state => state.isAuthenticated);

	const navigate = useNavigate();

	const handleProfileClick = (e: React.MouseEvent) => {
		e.preventDefault();
		saveDiscoveryState();
		if (!isAuthenticated) {
			setShowLoginPrompt(true);
		}
		else {
			navigate({ to: "/buyer/home" });
		}
	};

	const handleBagClick = (e: React.MouseEvent) => {
		e.preventDefault();
		saveDiscoveryState();
		navigate({ to: "/bag" });
	};

	const handleLoginRedirect = () => {
		// Store intended redirect in sessionStorage
		if (typeof window !== "undefined") {
			window.sessionStorage.setItem("afterLoginRedirect", "/buyer/home");
			window.location.href = "/sign-in";
		}
	};

	// // Removed unused scrollYRef
	// const handleNextPage = () => {
	// 	setPage(prev => prev + 1);
	// };
	// const handlePrevPage = () => {
	// 	setPage(prev => Math.max(1, prev - 1));
	// };
	// const handleLimitChange = (newLimit: number) => {
	// 	setLimit(newLimit);
	// 	setPage(1); // Reset to first page when changing items per page
	// };
	// const handlePageJump = (pageNum: number) => {
	// 	const totalPages = Math.ceil(totalItems / limit);
	// 	if (pageNum >= 1 && pageNum <= totalPages) {
	// 		setPage(pageNum);
	// 	}
	// };

	// Remove scroll restore logic since pagination is now explicit

	// (TabType definition moved to top-level for reuse)

	// Scroll to top button state
	const [showScrollTop, setShowScrollTop] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setShowScrollTop(window.scrollY > 300);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const handleScrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			{/* Navbar using reusable component */}
			<NavbarRefund
				searchValue={search}
				onSearchChange={setSearch}
				onProfileClick={handleProfileClick}
				onBagClick={handleBagClick}
				activeTab={activeTab}
				setActiveTab={(tab: TabType) => {
					// Save current category's scroll position before switching
					const currentKey = activeCategoryId || "all";
					if (categoryCacheRef.current[currentKey]) {
						categoryCacheRef.current[currentKey].scrollY = window.scrollY;
					}
					lastProductCountRef.current = 0; // Reset so auto-scroll doesn't fire on category switch
					setActiveTab(tab);
					setActiveCategoryId(tab.id === "All" ? "" : tab.id);
					setPage(1); // Reset to page 1 so cache lookup triggers
					window.scrollTo(0, 0); // Always scroll to top when switching categories
				}}
			/>
			{/* Login Modal using reusable component */}
			<LoginPromptModalDiscovery
				open={showLoginPrompt}
				onClose={() => setShowLoginPrompt(false)}
				onLogin={handleLoginRedirect}
			/>

			{/* Product Grid */}
			<main className="flex-1 px-2 sm:px-4 md:px-8 py-6 md:py-8 w-full">
				{loading && page === 1
					? (
							<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
								{Array.from({ length: 8 }).map((_, idx) => (
									<ProductCardSkeleton key={idx} />
								))}
							</div>
						)
					: error
						? (
								<div className="text-center text-red-500">{error}</div>
							)
						: (
								<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
									{products.length === 0
										? (
												<div className="col-span-full text-center text-gray-400">No products found.</div>
											)
										: (
												<>
													{products.map((product, idx) => (
														<div
															key={product.id || idx}
															data-product-card
															className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow"
															onClick={() => {
																saveDiscoveryState();
																navigate({ to: `/product/${product.id || idx}` });
															}}
															tabIndex={0}
															role="button"
															aria-label={`View details for ${product.name || "Product"}`}
															onKeyPress={(e) => {
																if (e.key === "Enter") {
																	saveDiscoveryState();
																	navigate({ to: `/product/${product.id || idx}` });
																}
															}}
														>
															<div className="bg-gray-200 w-full aspect-[4/5] overflow-hidden flex items-center justify-center">
																<img
																	src={(product.photos && product.photos.length > 0) ? product.photos[0] : "/public/assets/icons/shoe1.jpg"}
																	alt={product.name}
																	loading="lazy"
																	className="w-full h-full object-cover"
																	style={{ display: "block" }}
																/>
															</div>
															<div className="p-3 flex flex-col gap-1">
																<span className="text-xs text-blue-700 font-semibold" aria-label="Vendor name">{product.vendorId?.fullName || "NAME OF STORE"}</span>
																<h2 className="text-sm text-gray-800 m-0" style={{ margin: 0 }}>{product.name || "Product name"}</h2>
																<span className="text-sm font-bold text-gray-900">{product.price ? `₦${product.price}` : "₦--"}</span>
															</div>
														</div>
													))}
													{/* Skeleton cards while loading more */}
													{loading && page > 1 && (
														Array.from({ length: 8 }).map((_, idx) => (
															<ProductCardSkeleton key={`skeleton-${idx}`} />
														))
													)}
												</>
											)}
								</div>
							)}

				{/* See more button */}
				{products.length < totalItems && !loading && (
					<div className="flex flex-col items-center mt-8 gap-4">
						<span className="text-sm text-gray-700">
							Showing
							{" "}
							{products.length}
							{" "}
							of
							{" "}
							{totalItems}
							{" "}
							products
						</span>
						<Button
							onClick={() => {
								lastProductCountRef.current = products.length;
								setPage(prev => prev + 1);
							}}
							className="flex items-center gap-1.5 bg-[#3F2EC8] text-white px-3.5 py-3 rounded-full font-medium text-xs leading-[1.2] hover:bg-[#3426a8] transition-colors font-inter text-center"
						>
							See more
							<img src="/assets/icons/arrow-down.svg" alt="see more" className="flex items-center gap-2 w-5 h-5" />
						</Button>

					</div>
				)}
				{/* Pagination Controls */}
				{/* {products.length > 0 && !loading && (
					<div className="mt-8 space-y-6"> */}
				{/* Items per page and count display */}
				{/* <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4">
							<div className="flex items-center gap-2">
								<span className="text-sm text-gray-700">Items per page:</span>
								<select
									value={limit}
									onChange={e => handleLimitChange(Number(e.target.value))}
									className="border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] focus:border-transparent cursor-pointer"
								>
									<option value={10}>10</option>
									<option value={20}>20</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</div>
							<div className="text-sm text-gray-700 font-medium">
								Showing
								{" "}
								{((page - 1) * limit) + 1}
								-
								{Math.min(page * limit, totalItems)}
								{" "}
								of
								{" "}
								{totalItems}
								{" "}
								products
							</div>
						</div> */}
				{/* Page navigation */}
				{/* <div className="flex flex-wrap justify-center items-center gap-2">
							<button
								onClick={handlePrevPage}
								disabled={page === 1}
								className={`px-3 py-2 text-sm font-medium border rounded ${
									page === 1
										? "text-gray-400 border-gray-200 cursor-not-allowed"
										: "text-gray-700 border-gray-300 hover:bg-gray-50"
								}`}
							>
								&lt;
							</button>

							{(() => {
								const totalPages = Math.ceil(totalItems / limit);
								const pageButtons = [];
								const maxVisiblePages = 7;

								if (totalPages <= maxVisiblePages) {
								// Show all pages if total is small
									for (let i = 1; i <= totalPages; i++) {
										pageButtons.push(
											<button
												key={i}
												onClick={() => handlePageJump(i)}
												className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded ${
													page === i
														? "bg-[#3F2EC8] text-white"
														: "text-gray-700 hover:bg-gray-100 border border-gray-300"
												}`}
											>
												{i}
											</button>
										);
									}
								}
								else {
								// Always show first page
									pageButtons.push(
										<button
											key={1}
											onClick={() => handlePageJump(1)}
											className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded ${
												page === 1
													? "bg-[#3F2EC8] text-white"
													: "text-gray-700 hover:bg-gray-100 border border-gray-300"
											}`}
										>
											1
										</button>
									);

									// Show ellipsis if current page is far from start
									if (page > 3) {
										pageButtons.push(
											<span key="ellipsis1" className="px-2 text-gray-500">...</span>
										);
									}

									// Show pages around current page
									const startPage = Math.max(2, page - 1);
									const endPage = Math.min(totalPages - 1, page + 1);

									for (let i = startPage; i <= endPage; i++) {
										pageButtons.push(
											<button
												key={i}
												onClick={() => handlePageJump(i)}
												className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded ${
													page === i
														? "bg-[#3F2EC8] text-white"
														: "text-gray-700 hover:bg-gray-100 border border-gray-300"
												}`}
											>
												{i}
											</button>
										);
									}

									// Show ellipsis if current page is far from end
									if (page < totalPages - 2) {
										pageButtons.push(
											<span key="ellipsis2" className="px-2 text-gray-500">...</span>
										);
									}

									// Always show last page
									pageButtons.push(
										<button
											key={totalPages}
											onClick={() => handlePageJump(totalPages)}
											className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded ${
												page === totalPages
													? "bg-[#3F2EC8] text-white"
													: "text-gray-700 hover:bg-gray-100 border border-gray-300"
											}`}
										>
											{totalPages}
										</button>
									);
								}

								return pageButtons;
							})()}

							<button
								onClick={handleNextPage}
								disabled={products.length < limit || (page * limit) >= totalItems}
								className={`px-3 py-2 text-sm font-medium rounded ${
									products.length < limit || (page * limit) >= totalItems
										? "text-white bg-gray-400 cursor-not-allowed"
										: "text-white bg-[#3F2EC8] hover:bg-[#3426a8]"
								}`}
							>
								&gt;
							</button>
						</div>
					</div>
				)} */}

				{/* Scroll to top button */}
				{showScrollTop && (
					<Button
						onClick={handleScrollToTop}
						aria-label="Scroll to top"
						className="fixed bottom-20 right-4 z-50 bg-[#3F2EC8] text-white rounded-full shadow-lg w-12 h-12 flex items-center justify-center transition-opacity duration-300 md:bottom-40 md:right-10"
						style={{ paddingBottom: "env(safe-area-inset-bottom)", backgroundColor: "#3F2EC8" }}
						size="icon"
					>
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-chevron-up" aria-hidden="true"><polyline points="18 15 12 9 6 15"></polyline></svg>
					</Button>
				)}
			</main>
			{/* Footer */}
			<DiscoveryFooter />
		</div>
	);
}

export const Route = createFileRoute("/discovery")({
	component: Discovery
});
